// src/utils/localeDisplay.ts
export type UnitSystem = "Metric" | "Imperial";
export type CurrencyCode =
	| "INR"
	| "USD"
	| "GBP"
	| "CAD"
	| "EUR"
	| "SGD"
	| "AUD"
	| "AED";

export type LocalePrefs = {
	currency?: CurrencyCode; // e.g. "USD"
	unitSystem?: UnitSystem; // "Metric" | "Imperial"
};

// --------------------
// Fixed exchange rates (base INR)
// --------------------
const INR_TO: Record<CurrencyCode, number> = {
	INR: 1,
	USD: 1 / 83.0,
	GBP: 1 / 105.0,
	CAD: 1 / 61.5,
	EUR: 1 / 90.0,
	SGD: 1 / 62.0,
	AUD: 1 / 55.0,
	AED: 1 / 22.6,
};

const SYMBOL: Record<CurrencyCode, string> = {
	INR: "₹",
	USD: "$",
	GBP: "£",
	CAD: "CA$",
	EUR: "€",
	SGD: "S$",
	AUD: "A$",
	AED: "د.إ",
};

const MM_PER_INCH = 25.4;
const LB_PER_KG = 2.2046226218;
const IN2_PER_MM2 = 1 / 645.16; // 1 in² = 645.16 mm²

function safeNum(n: any): number | null {
	const v = Number(n);
	return Number.isFinite(v) ? v : null;
}

function round(n: number, dp = 2) {
	const p = 10 ** dp;
	return Math.round(n * p) / p;
}

export function convertFromINR(amountINR: number, to: CurrencyCode) {
	return amountINR * (INR_TO[to] ?? 1);
}

export function formatMoneyFromINR(
	amountINR: number | null | undefined,
	prefs?: LocalePrefs,
	dp = 2,
) {
	const v = safeNum(amountINR);
	if (v == null) return "";
	const currency = (prefs?.currency ?? "INR") as CurrencyCode;
	const converted = convertFromINR(v, currency);
	return `${SYMBOL[currency] ?? ""} ${round(converted, dp).toLocaleString()}`;
}

// ---- Units: base assumed as mm / kg / mm² in your data ----
export function formatLengthFromMM(
	mm: number | null | undefined,
	prefs?: LocalePrefs,
	dp = 2,
) {
	const v = safeNum(mm);
	if (v == null) return "";
	if (prefs?.unitSystem === "Imperial") {
		const inch = v / MM_PER_INCH;
		return `${round(inch, dp)} in`;
	}
	return `${round(v, dp)} mm`;
}

export function formatMassFromKG(
	kg: number | null | undefined,
	prefs?: LocalePrefs,
	dp = 3,
) {
	const v = safeNum(kg);
	if (v == null) return "";
	if (prefs?.unitSystem === "Imperial") {
		const lb = v * LB_PER_KG;
		return `${round(lb, dp)} lb`;
	}
	return `${round(v, dp)} kg`;
}

export function formatAreaFromMM2(
	mm2: number | null | undefined,
	prefs?: LocalePrefs,
	dp = 2,
) {
	const v = safeNum(mm2);
	if (v == null) return "";
	if (prefs?.unitSystem === "Imperial") {
		const in2 = v * IN2_PER_MM2;
		return `${round(in2, dp)} in²`;
	}
	return `${round(v, dp)} mm²`;
}

// Convenience helpers for “A * B * C” strings (values are in mm)
export function formatDimProductFromMM(
	valuesMM: Array<number | null | undefined>,
	prefs?: LocalePrefs,
) {
	const parts = valuesMM
		.map((x) => {
			const v = safeNum(x);
			if (v == null) return "";
			return prefs?.unitSystem === "Imperial"
				? `${round(v / MM_PER_INCH, 2)}`
				: `${round(v, 2)}`;
		})
		.filter(Boolean);

	if (!parts.length) return "";
	const unit = prefs?.unitSystem === "Imperial" ? "in" : "mm";
	return `${parts.join(" * ")} ${unit}`;
}
